/**
 * feat-300 — closed threads under a card (design NOTES §3.8; ADR §10.8).
 *
 * One line per `applied` | `discarded` thread (never `superseded`), newest
 * first: "AI edit · Applied · 2 Oct · email". Three shown, then "Show n more".
 * Closed threads arrive WITHOUT messages, so expanding one fetches
 * `GET /question-edit-threads/{id}` and renders it read-only. If the list said
 * `closed_truncated`, history is best-effort — nothing here claims "all".
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import { QuizQuestion } from '../../../../services/quizContentAdminService';
import { QuestionEditThread } from '../../../../services/questionEditService';
import { useQuestionEdit } from './QuestionEditProvider';
import { WorkingSpinner } from './MicButton';
import ThreadLog from './ThreadLog';
import { HINT, HIST_BTN, HIST_EXPANDED, HIST_WRAP } from './classes';
import { historyDate } from './formatWhen';
import { QUESTION_EDIT_COPY as COPY } from './voiceEditCopy';

const SHOWN = 3;

export const HISTORY_ATTR = 'data-qe-history';

export function historySentence(t: QuestionEditThread): string {
  const outcome =
    t.state === 'applied' ? COPY.historyOutcome.applied : COPY.historyOutcome.discarded;
  return COPY.historyLine(outcome, historyDate(t.closed_at ?? t.updated_at), t.closed_by_email ?? '');
}

const HistoryLine: React.FC<{ t: QuestionEditThread; q: QuizQuestion }> = ({ t, q }) => {
  const ctx = useQuestionEdit()!;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const sentence = historySentence(t);
  const Chevron = open ? ChevronDown : ChevronRight;
  const regionId = `qe-hist-${t.id}`;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !t.messages) {
      setLoading(true);
      setFailed(false);
      try {
        await ctx.fetchThread(t.id);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="max-w-full w-full">
      <button
        type="button"
        className={HIST_BTN}
        aria-expanded={open}
        aria-controls={open ? regionId : undefined}
        // Security M3: no `title` — the sentence carries an email, and Replay never
        // masks attributes while maskAllText is false (CoverageMatrix precedent).
        onClick={toggle}
        {...{ [HISTORY_ATTR]: t.id }}
      >
        <Chevron className="w-3 h-3 shrink-0" aria-hidden />
        <History className="w-3.5 h-3.5 shrink-0" aria-hidden />
        <span data-sentry-mask className="truncate" data-testid="qe-history-sentence">
          {sentence}
        </span>
      </button>
      {open && (
        <div id={regionId} className={HIST_EXPANDED} data-testid="qe-history-expanded">
          {loading && (
            <p className={`${HINT} flex items-center gap-1.5`} aria-busy="true">
              <WorkingSpinner /> {COPY.historyLoading}
            </p>
          )}
          {failed && <p className={HINT}>{COPY.actionFailed}</p>}
          {t.messages && (
            <ThreadLog
              thread={t}
              q={q}
              currentEmail={ctx.currentEmail}
              readOnly
              approvalRequired={ctx.approvalRequired}
            />
          )}
        </div>
      )}
    </div>
  );
};

const HistoryLines: React.FC<{ q: QuizQuestion; exclude: string | null }> = ({ q, exclude }) => {
  const ctx = useQuestionEdit()!;
  const [showAll, setShowAll] = useState(false);
  const closed = ctx
    .threadsFor(q.id)
    .filter((t) => (t.state === 'applied' || t.state === 'discarded') && t.id !== exclude)
    .sort((a, b) => ((a.closed_at ?? a.updated_at) < (b.closed_at ?? b.updated_at) ? 1 : -1));
  if (!closed.length) return null;
  const shown = showAll ? closed : closed.slice(0, SHOWN);
  const more = closed.length - shown.length;
  return (
    <div className={`${HIST_WRAP} not-italic font-normal`} data-testid={`qe-history-${q.id}`}>
      {shown.map((t) => (
        <HistoryLine key={t.id} t={t} q={q} />
      ))}
      {more > 0 && (
        <button type="button" className={`${HIST_BTN} pl-[18px]`} onClick={() => setShowAll(true)}>
          {COPY.showMoreHistory(more)}
        </button>
      )}
    </div>
  );
};

export default HistoryLines;
